/**
 * INTERAX DESIGN SYSTEM COLORS
 * Simple, clean color palette with only the essential colors
 * as specified in the design system requirements.
 */

// Primary Colors
export const OFF_BLACK = '#212629'
export const HERITAGE_BLUE = '#2E4F5F'
export const THUNDER_GREY = '#78909C'
export const SUN = '#FFAB40'

// Status Colors (Design system semantic tokens)
export const GREEN = '#10B981' // success green
export const GREEN_BACKGROUND = 'rgba(16, 185, 129, 0.2)' // green 20%
export const BLUE = '#3B82F6' // info blue
export const BLUE_BACKGROUND = 'rgba(59, 130, 246, 0.2)' // blue 20%

// Secondary Colors (Sun variations)
export const SUN_40 = 'rgba(255, 171, 64, 0.4)' // Sun 40%
export const SUN_20 = 'rgba(255, 171, 64, 0.2)' // Sun 20%
export const SUN_10 = 'rgba(255, 171, 64, 0.1)' // Sun 10%

// Thunder Grey variations
export const THUNDER_GREY_20 = 'rgba(120, 144, 156, 0.2)' // Thunder Grey 20%

// Off Colors (Heritage Blue variations)
export const HERITAGE_BLUE_40 = 'rgba(46, 79, 95, 0.4)' // Heritage Blue 40%
export const HERITAGE_BLUE_RADIAL = 'radial-gradient(circle, rgba(46, 79, 95, 0.6) 0%, rgba(46, 79, 95, 0.2) 100%)'

// White Variations
export const WHITE = '#FFFFFF'
export const WHITE_30 = 'rgba(255, 255, 255, 0.3)' // White 30%
export const WHITE_20 = 'rgba(255, 255, 255, 0.2)' // White 20%
export const WHITE_10 = 'rgba(255, 255, 255, 0.1)' // White 10%

// Black Variations
export const BLACK_TRANSPARENT_BORDER = 'rgba(0, 0, 0, 0.6)' // Black 60% opacity

/**
 * Primary Colors Object
 */
export const PRIMARY_COLORS = {
  offBlack: OFF_BLACK,
  heritageBlue: HERITAGE_BLUE,
  thunderGrey: THUNDER_GREY,
  sun: SUN
} as const

/**
 * Secondary Colors Object (Sun variations)
 */
export const SECONDARY_COLORS = {
  sun: SUN,
  sun40: SUN_40,
  sun20: SUN_20,
  sun10: SUN_10
} as const

/**
 * Off Colors Object (Heritage Blue variations)
 */
export const OFF_COLORS = {
  heritageBlue: HERITAGE_BLUE,
  heritageBlue40: HERITAGE_BLUE_40,
  heritageBlueRadial: HERITAGE_BLUE_RADIAL
} as const

/**
 * White Variations Object
 */
export const WHITE_COLORS = {
  white: WHITE,
  white30: WHITE_30,
  white20: WHITE_20,
  white10: WHITE_10
} as const

/**
 * Black Variations Object
 */
export const BLACK_COLORS = {
  blackTransparentBorder: BLACK_TRANSPARENT_BORDER
} as const

/**
 * Glassy Action Button Gradients
 * Premium gradient colors for 3D embossed action buttons
 */
export const GLASSY_BUTTON_GRADIENTS = {
  // Orange variant - for primary actions
  orangeOuter: 'linear-gradient(360deg, #FFAB40 0%, #FFBC66 100%)',
  orangeInner: 'linear-gradient(180deg, #FFAB40 0%, #FFBC66 100%)',
  // Gray variant - for secondary actions
  grayOuter: 'linear-gradient(360deg, #7C7C7C 0%, #CECECE 100%)',
  grayInner: 'linear-gradient(180deg, #8F8F8F 0%, #CBCBCB 100%)'
} as const

/**
 * Sidebar Menu Item Gradients
 * Text gradients for active sidebar items
 */
export const SIDEBAR_GRADIENTS = {
  // Active menu item text gradient
  activeMenuText: 'linear-gradient(180deg, #FFFFFF 0%, #A1B1BA 100%)',
  // Active menu item background (simple version)
  activeBackgroundSimple: 'rgba(255, 171, 64, 0.1)',
  // Active menu item background (complex version)
  activeBackgroundComplex: '#212629',
  // Active indicator glow color
  activeIndicatorGlow: '#FFAB40'
} as const

/**
 * All Colors Combined
 */
export const INTERAX_COLORS = {
  ...PRIMARY_COLORS,
  ...SECONDARY_COLORS,
  ...OFF_COLORS,
  ...WHITE_COLORS,
  ...BLACK_COLORS,
  // Status semantic tokens
  green: GREEN,
  greenBackground: GREEN_BACKGROUND,
  blue: BLUE,
  blueBackground: BLUE_BACKGROUND,
  thunderGrey20: THUNDER_GREY_20,
  // Glassy button gradients
  ...GLASSY_BUTTON_GRADIENTS,
  // Sidebar gradients
  ...SIDEBAR_GRADIENTS
} as const

/**
 * Typography Configuration
 * Using Lexend Deca as primary font and system fonts as secondary
 */

// Font Families
export const FONT_FAMILY_PRIMARY = 'Lexend Deca, sans-serif'
export const FONT_FAMILY_SECONDARY = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

/**
 * Typography Styles
 */
export const TYPOGRAPHY = {
  // Headlines
  headline1: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '600', // Semi Bold
    fontSize: '64px',
    lineHeight: '76px'
  },
  headline2: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '600', // Semi Bold
    fontSize: '36px',
    lineHeight: '44px'
  },
  headline3: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '600', // Semi Bold
    fontSize: '28px',
    lineHeight: '36px'
  },
  headline4: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '600', // Semi Bold
    fontSize: '20px',
    lineHeight: '28px'
  },
  headline5: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '600', // Semi Bold
    fontSize: '18px',
    lineHeight: '26px'
  },
  headline6: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '600', // Semi Bold
    fontSize: '16px',
    lineHeight: '24px'
  },
  // Body Text
  body1: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '400', // Regular
    fontSize: '18px',
    lineHeight: '28px'
  },
  body2: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '400', // Regular
    fontSize: '16px',
    lineHeight: '24px'
  },
  body3: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '400', // Regular
    fontSize: '14px',
    lineHeight: '20px'
  },
  // Button/Link Text
  buttonNormal: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '500', // Medium
    fontSize: '18px',
    lineHeight: '24px'
  },
  buttonMedium: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '500', // Medium
    fontSize: '16px',
    lineHeight: '20px'
  },
  buttonSmall: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '500', // Medium
    fontSize: '14px',
    lineHeight: '16px'
  },
  // Menu Items
  menuNonSelected: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '400', // Regular
    fontSize: '14px',
    lineHeight: '20px'
  },
  menuSelected: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontWeight: '500', // Medium
    fontSize: '14px',
    lineHeight: '20px'
  }
} as const

/**
 * Font Weights
 */
export const FONT_WEIGHTS = {
  regular: '400',
  medium: '500',
  semibold: '600'
} as const

/**
 * Font Sizes
 */
export const FONT_SIZES = {
  headline1: '64px',
  headline2: '36px',
  headline3: '28px',
  headline4: '20px',
  body1: '18px',
  body2: '16px',
  body3: '14px',
  buttonNormal: '18px',
  buttonMedium: '16px',
  buttonSmall: '14px'
} as const

/**
 * Line Heights
 */
export const LINE_HEIGHTS = {
  headline1: '76px',
  headline2: '44px',
  headline3: '36px',
  headline4: '28px',
  body1: '28px',
  body2: '24px',
  body3: '20px',
  buttonNormal: '24px',
  buttonMedium: '20px',
  buttonSmall: '16px'
} as const

// Legacy exports for backward compatibility (will be removed gradually)
export const INTERAX_TYPOGRAPHY = TYPOGRAPHY
